/**
 * GrowthTypes.ts
 * Types for the dungeon growth system.
 * Dungeons are living entities that evolve over time in poqpoq World.
 * Growth rules define triggers, actions, and constraints.
 */

// ─── Dungeon Personality ────────────────────────────────────────

export type DungeonPersonality =
    | 'aggressive'    // Spawns more enemies, harder fights
    | 'cunning'       // More traps, hidden passages, puzzles
    | 'hoarding'      // More treasure, but better guarded
    | 'expansive'     // Grows rooms and corridors rapidly
    | 'defensive'     // Reinforces existing rooms, adds barriers

export interface DungeonDNA {
    personality: DungeonPersonality
    growthRate: number
    maxRooms: number
    maxLevels: number
    preferredRoomTypes: string[]
    enemyAffinity: string[]
    trapAffinity: string[]
    rules: GrowthRule[]
}

// ─── Growth Rules ───────────────────────────────────────────────

export type GrowthTriggerType =
    | 'time_elapsed'
    | 'players_defeated'
    | 'boss_killed'
    | 'akashic_energy'
    | 'seed_planted'
    | 'donation_received'
    | 'rooms_cleared'

export interface GrowthTrigger {
    type: GrowthTriggerType
    threshold: number
}

export type GrowthActionType =
    | 'expand_room'
    | 'add_corridor'
    | 'upgrade_enemies'
    | 'add_trap'
    | 'grow_treasure'
    | 'evolve_boss'
    | 'unlock_level'
    | 'spawn_miniboss'
    | 'add_shrine'
    | 'shift_walls'

export interface GrowthAction {
    type: GrowthActionType
    params: Record<string, unknown>
}

export interface GrowthRule {
    id: string
    name: string
    trigger: GrowthTrigger
    action: GrowthAction
    cooldownHours: number
    maxOccurrences: number
    currentOccurrences: number
}

// ─── Presets ────────────────────────────────────────────────────

export const PERSONALITY_PRESETS: Record<DungeonPersonality, Partial<DungeonDNA>> = {
    aggressive: {
        growthRate: 1.5,
        preferredRoomTypes: ['boss_arena', 'trap_room'],
        enemyAffinity: ['warrior', 'berserker', 'golem'],
        trapAffinity: ['spike', 'boulder']
    },
    cunning: {
        growthRate: 1.0,
        preferredRoomTypes: ['trap_room', 'chamber'],
        enemyAffinity: ['rogue', 'illusionist', 'mimic'],
        trapAffinity: ['pit', 'poison', 'alarm', 'magical']
    },
    hoarding: {
        growthRate: 0.8,
        preferredRoomTypes: ['vault', 'treasure_room'],
        enemyAffinity: ['guardian', 'sentinel', 'dragon'],
        trapAffinity: ['magical', 'alarm']
    },
    expansive: {
        growthRate: 2.0,
        preferredRoomTypes: ['cavern', 'chamber'],
        enemyAffinity: ['minion', 'swarm', 'elemental'],
        trapAffinity: ['pit', 'arrow']
    },
    defensive: {
        growthRate: 0.6,
        preferredRoomTypes: ['chamber', 'shrine'],
        enemyAffinity: ['guardian', 'golem', 'construct'],
        trapAffinity: ['arrow', 'boulder', 'spike']
    }
}

export function createDefaultDNA(personality: DungeonPersonality = 'expansive'): DungeonDNA {
    const preset = PERSONALITY_PRESETS[personality]
    return {
        personality,
        growthRate: preset.growthRate ?? 1.0,
        maxRooms: 30,
        maxLevels: 3,
        preferredRoomTypes: preset.preferredRoomTypes ?? ['cavern', 'chamber'],
        enemyAffinity: preset.enemyAffinity ?? ['minion'],
        trapAffinity: preset.trapAffinity ?? ['pit'],
        rules: []
    }
}
